import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import useResizeObserver from 'use-resize-observer'
import {Lang} from 'yougile-lang'
import {Backend} from '@common/backend'
import {
  getOptions,
  unsplashLink
} from '@components/board-appearance/board-appearance'
import {TagItem} from '@components/board-appearance/tag-item'
import {unsplashTags} from '@components/board-appearance/unsplash/tags'
import {UnsplashColor} from '@components/board-appearance/unsplash/types'
import {unsplashStore} from '@components/board-appearance/unsplash/unsplash-store'
import {wrapUrlUtm} from '@components/board-appearance/unsplash/wrap-url-utm'
import {Button, Link, LinkButton} from '@components/common/base/buttons'
import {Input} from '@components/common/base/input'
import {SelectWithDropdown} from '@components/common/base/select-with-dropdown'
import {menuItemHeight} from '@components/movement-templates/constants'
import {useSyncEvent} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {useLang} from '@extensions/use-lang'
import {TwTitlePrimary, TwTitleSecondary} from './containers'
import {TwTitle} from './containers'
import BackgroundIcon from './icons/icon-background.svg'
import {ProfileSkinContent} from './profile-skin-content'
import {
  ProfileSkinContext,
  ProfileSkinStore,
  useProfileSkinStore
} from './profile-skin-store'

const TwContent = tw.div<{$disabled?: boolean}>`
  pt-8
  flex
  items-start
  flex-col

  ${p => (p.$disabled ? 'opacity-30 pointer-events-none' : '')}
`

export const Skin = () => {
  const [store] = useState(() => new ProfileSkinStore())

  return (
    <ProfileSkinContext.Provider value={store}>
      <SkinBase />
    </ProfileSkinContext.Provider>
  )
}

export const SkinBase = observer(() => {
  const {ref, width = 1} = useResizeObserver()
  const [state, store] = useProfileSkinStore()

  useLang()

  useEffect(() => {
    store.recalcByWidth(width)
  }, [width])

  useSyncEvent(
    'profile',
    {
      debounceTime: 50,
      filter: globals.currentUserId
    },
    () => {
      store.calculateCustomSkins()
    }
  )

  const searchOn = unsplashStore.isSearch

  useEffect(() => {
    store.syncNew()
  }, [])

  useEffect(() => {
    unsplashStore.resetSearch()
  }, [])

  const loadBg = (event: React.MouseEvent) => {
    unsplashStore.resetSearch()
    store.loadBg(event)
  }

  const options = getOptions()
  const themeValue =
    options.find(o => o.key === unsplashStore.state.unsplashColor)?.text || ''

  const supported = !!Backend.features?.unsplash

  return (
    <div className="flex flex-col">
      <TwTitle className="mb-8">
        <div className="flex mr-4 text-primary">
          <BackgroundIcon />
        </div>
        {Lang.msg.profilePage.chooseBackground}:
      </TwTitle>

      <TwContent ref={ref}>
        <div className="mb-16 flex items-center gap-16">
          <LinkButton onClick={loadBg} icon={'IconCustomPlus'} noPaddings>
            {Lang.msg.boardAppearance.uploadCustomBg}
          </LinkButton>
          {searchOn && (
            <LinkButton onClick={() => unsplashStore.resetSearch()} noPaddings>
              {Lang.msg.boardAppearance.backToSaved}
            </LinkButton>
          )}
        </div>

        {supported && (
          <div className="flex items-center mb-8 gap-8">
            <Input
              value={unsplashStore.state.search}
              onChange={({text}) => unsplashStore.setSearch(text, 'profile')}
              placeholder={Lang.msg.profilePage.unsplashPlaceholder}
              size="32"
              className="!w-440"
              style={{maxWidth: `${state.skinsWidth}px`}}
              onSubmit={() => unsplashStore.doSearch()}
            />
            <Button size="small" onClick={unsplashStore.doSearch}>
              {Lang.msg.profilePage.buttonSearch}
            </Button>
          </div>
        )}

        {supported && (
          <div className="flex gap-8 items-center mb-16">
            <TwTitleSecondary>
              {Lang.msg.boardAppearance.searchExamples}
            </TwTitleSecondary>

            {unsplashTags.map(tag => (
              <TagItem
                key={tag}
                text={tag}
                onClick={() => {
                  unsplashStore.setSearch(tag, 'profile')
                  unsplashStore.doSearch()
                }}
              />
            ))}
          </div>
        )}

        <div className="flex flex-col gap-16 w-full">
          {unsplashStore.state.search && (
            <div className="flex gap-4">
              <TwTitlePrimary>{Lang.msg.boardAppearance.photos}</TwTitlePrimary>
              <Link style={{fontWeight: 600}} href={wrapUrlUtm(unsplashLink)}>
                {'Unsplash'}
              </Link>
            </div>
          )}
          {unsplashStore.state.search && (
            <div className="flex items-center gap-16 ">
              <TwTitleSecondary>
                {Lang.msg.boardAppearance.theme}
              </TwTitleSecondary>
              <div>
                <SelectWithDropdown
                  options={options}
                  value={themeValue}
                  size={menuItemHeight}
                  width={'min-w-230 max-w-230'}
                  onChange={val =>
                    unsplashStore.searchWithColor(val.key as UnsplashColor)
                  }
                />
              </div>
            </div>
          )}

          <ProfileSkinContent />
        </div>
      </TwContent>
    </div>
  )
})
